/**
 * Natural Language Insight Generator
 * 
 * Transforms raw behavioral data into conversational,
 * human-readable insight statements that make users
 * feel understood.
 */

import type {
  BehavioralInsightsData,
  DayPattern,
  PageCountInsight,
  FormatInsight,
  DNFInsight,
  GenreVelocityInsight,
  SeasonalPattern
} from './behavioralInsightsUtils';

// ============= Types =============

export type InsightType = 
  | 'weekend_warrior' 
  | 'peak_day' 
  | 'sweet_spot' 
  | 'long_book_struggle' 
  | 'format_champion' 
  | 'format_speed' 
  | 'abandonment_point' 
  | 'genre_dnf' 
  | 'genre_velocity' 
  | 'seasonal_peak';

export interface GeneratedInsight {
  id: InsightType;
  icon: string;
  title: string;
  description: string;
  cta?: {
    text: string;
    action: 'discover' | 'chat';
    context?: string;
  };
  confidence: number; // 0-100, used for sorting
}

// ============= Insight Generators =============

function generateDayPatternInsights(data: DayPattern): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  if (data.isWeekendReader) {
    const weekendPercentage = Math.round(
      ((data.distribution[0] + data.distribution[6]) / data.totalBooks) * 100
    );
    
    insights.push({
      id: 'weekend_warrior',
      icon: '📅',
      title: 'Weekend Warrior',
      description: `You finish ${weekendPercentage}% of your books on Saturday or Sunday. Your reading thrives when you have time to unwind.`,
      cta: {
        text: 'Find a perfect weekend read',
        action: 'chat',
        context: 'Recommend a book perfect for a relaxed weekend reading session'
      },
      confidence: Math.min(weekendPercentage + data.totalBooks, 95)
    });
  } else if (data.peakPercentage >= 20) {
    insights.push({
      id: 'peak_day',
      icon: '📆',
      title: `${data.peakDay} Reader`,
      description: `Your reading peaks on ${data.peakDay}s – you finish more books then than any other day of the week.`,
      cta: {
        text: `Save a book for ${data.peakDay}`,
        action: 'chat',
        context: `Recommend a great book to read on a ${data.peakDay}`
      },
      confidence: data.peakPercentage + 40
    });
  }

  return insights;
}

function generatePageCountInsights(data: PageCountInsight): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  // Sweet spot insight
  const rangeLabel = data.sweetSpotRange[1] === Infinity 
    ? `${data.sweetSpotRange[0]}+` 
    : `${data.sweetSpotRange[0]}-${data.sweetSpotRange[1]}`;

  if (data.avgRatingInRange > data.avgRatingOutside + 0.3) {
    insights.push({
      id: 'sweet_spot',
      icon: '📖',
      title: 'Your Sweet Spot',
      description: `Books with ${rangeLabel} pages get ${data.avgRatingInRange}⭐ from you vs ${data.avgRatingOutside}⭐ for others. You know what length works for you.`,
      cta: {
        text: 'Find books in your sweet spot',
        action: 'chat',
        context: `Recommend highly-rated books between ${data.sweetSpotRange[0]} and ${data.sweetSpotRange[1]} pages`
      },
      confidence: 85
    });
  }

  // Long book struggle
  if (data.dnfRateAbove >= 30) {
    insights.push({
      id: 'long_book_struggle',
      icon: '🧱',
      title: 'The Long Book Wall',
      description: `Long books are your kryptonite – you abandon ${data.dnfRateAbove}% of books over ${data.threshold} pages. Consider breaking them into chunks or trying audio.`,
      cta: {
        text: 'Try a shorter read',
        action: 'chat',
        context: 'Recommend highly-rated books under 350 pages that are easy to finish'
      },
      confidence: data.dnfRateAbove + 40
    });
  }

  return insights;
}

function generateFormatInsights(data: FormatInsight): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  // Find best performing format
  const formats = Object.entries(data.formatCompletion);
  const bestFormat = formats.reduce((best, [fmt, stats]) => {
    return stats.rate > (data.formatCompletion[best]?.rate || 0) ? fmt : best;
  }, formats[0]?.[0] || '');

  const bestFormatStats = data.formatCompletion[bestFormat];
  
  if (bestFormatStats && data.secondFormat && data.secondFormatRate !== undefined) {
    const rateDiff = bestFormatStats.rate - data.secondFormatRate;
    
    if (rateDiff >= 15 || bestFormatStats.rate >= 90) {
      const formatName = formatLabel(bestFormat);
      
      insights.push({
        id: 'format_champion',
        icon: bestFormat === 'audio' ? '🎧' : bestFormat === 'digital' ? '📱' : '📚',
        title: `${formatName} Champion`,
        description: `${formatName} keep${bestFormat === 'audio' ? '' : 's'} you going – you've finished ${bestFormatStats.rate}% of them${data.secondFormat ? ` vs ${data.secondFormatRate}% for ${formatLabel(data.secondFormat).toLowerCase()}` : ''}.`,
        cta: {
          text: `Browse ${formatName.toLowerCase()}`,
          action: 'chat',
          context: `Recommend books available in ${bestFormat} format`
        },
        confidence: bestFormatStats.rate
      });
    }
  }

  return insights;
}

function generateDNFInsights(data: DNFInsight): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  // Abandonment point
  if (data.avgAbandonmentPoint > 0) {
    insights.push({
      id: 'abandonment_point',
      icon: '🚪',
      title: 'The Exit Door',
      description: `When you give up on a book, it's usually around the ${data.avgAbandonmentPoint}% mark. Trust your instincts – life's too short for books that don't click.`,
      confidence: 70
    });
  }

  // Genre with highest DNF
  if (data.genreWithHighestDNF && data.genreDNFRate && data.genreDNFRate >= 40) {
    insights.push({
      id: 'genre_dnf',
      icon: '⚠️',
      title: `${data.genreWithHighestDNF} Struggles`,
      description: `You abandon ${data.genreDNFRate}% of ${data.genreWithHighestDNF} books. Maybe try a different subgenre or author in this space?`,
      cta: {
        text: `Find ${data.genreWithHighestDNF} you'll finish`,
        action: 'chat',
        context: `Recommend ${data.genreWithHighestDNF} books that are highly engaging and hard to put down`
      },
      confidence: data.genreDNFRate + 30
    });
  }

  return insights;
}

function generateGenreVelocityInsights(data: GenreVelocityInsight): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  const speedRatio = data.slowestDays / data.fastestDays;

  if (speedRatio >= 2) {
    insights.push({
      id: 'genre_velocity',
      icon: '⚡',
      title: 'Genre Speedster',
      description: `You devour ${data.fastestGenre} in ${data.fastestDays} days on average, but savor ${data.slowestGenre} for ${data.slowestDays} days. Different genres, different paces.`,
      cta: {
        text: `More ${data.fastestGenre} for a quick read`,
        action: 'chat',
        context: `Recommend ${data.fastestGenre} books for when I want to read something fast`
      },
      confidence: Math.min(speedRatio * 25, 90)
    });
  }

  return insights;
}

function generateSeasonalInsights(data: SeasonalPattern): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  insights.push({
    id: 'seasonal_peak',
    icon: '📊',
    title: 'Seasonal Reader',
    description: `Your reading peaks in ${data.peakMonth} (${data.peakCount} books) and dips in ${data.slumpMonth} (${data.slumpCount} books). Embrace your rhythm.`,
    confidence: 60
  });

  return insights;
}

// ============= Main Generation Function =============

/**
 * Generate all natural language insights from behavioral data
 * Returns insights sorted by confidence
 */
export function generateAllInsights(data: BehavioralInsightsData): GeneratedInsight[] {
  const insights: GeneratedInsight[] = [];

  if (data.dayPattern) {
    insights.push(...generateDayPatternInsights(data.dayPattern));
  }

  if (data.pageCountInsight?.hasEnoughData) {
    insights.push(...generatePageCountInsights(data.pageCountInsight));
  }

  if (data.formatInsight?.hasEnoughData) {
    insights.push(...generateFormatInsights(data.formatInsight));
  }

  if (data.dnfInsight?.hasEnoughData) {
    insights.push(...generateDNFInsights(data.dnfInsight));
  }

  if (data.genreVelocity?.hasEnoughData) {
    insights.push(...generateGenreVelocityInsights(data.genreVelocity));
  }

  if (data.seasonalPattern?.hasEnoughData) {
    insights.push(...generateSeasonalInsights(data.seasonalPattern));
  }

  // Sort by confidence (highest first) and limit to top 6
  return insights
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 6);
}

// ============= Helpers =============

function formatLabel(format: string): string {
  const labels: Record<string, string> = {
    print: 'Print books',
    digital: 'E-books',
    audio: 'Audiobooks'
  };
  return labels[format] || format;
}
